/**
 * Date Formatting Utilities
 * 
 * Helper functions for formatting dates, especially Firestore Timestamp objects
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Type guard to check if a value is a Firestore Timestamp
 */
function isFirestoreTimestamp(value: any): value is Timestamp {
  return value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function';
}

/**
 * Convert various date formats (Date, Firestore Timestamp, string) to a Date object
 * @param date - The date value to convert
 * @returns Date object or null if conversion fails
 */
export function toDate(date: Date | Timestamp | string | null | undefined): Date | null {
  if (!date) return null;
  
  try {
    if (date instanceof Date) {
      return date;
    }
    
    if (isFirestoreTimestamp(date)) {
      return date.toDate();
    }
    
    if (typeof date === 'string') {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to convert date:', error);
    return null;
  }
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "3 days ago")
 * @param date - The date to format (Date, Timestamp, or string)
 * @returns Relative time string
 */
export function formatRelativeTime(date: Date | Timestamp | string | null | undefined): string {
  const dateObj = toDate(date);
  if (!dateObj) return 'Recently';
  
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  
  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes === 1) return '1 minute ago';
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks === 1) return '1 week ago';
  if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
  if (diffMonths === 1) return '1 month ago';
  if (diffMonths < 12) return `${diffMonths} months ago`;
  if (diffYears === 1) return '1 year ago';
  return `${diffYears} years ago`;
}

/**
 * Format a Firestore date with fallback handling
 * @param date - The date to format (Date, Timestamp, or string)
 * @param options - Intl.DateTimeFormat options
 * @param fallback - Fallback text if date is invalid (default: 'Recently')
 * @returns Formatted date string or fallback text
 */
export function formatFirestoreDate(
  date: Date | Timestamp | string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  fallback: string = 'Recently'
): string {
  const dateObj = toDate(date);
  
  if (!dateObj) {
    return fallback;
  }
  
  try {
    return new Intl.DateTimeFormat('en-US', options).format(dateObj);
  } catch (error) {
    console.warn('Failed to format date:', error);
    return fallback;
  }
}

/**
 * Format date for article listings (e.g., "Jan 15, 2024, 10:30 AM")
 */
export function formatArticleDate(date: Date | Timestamp | string | null | undefined): string {
  return formatFirestoreDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format date for review history (e.g., "Jan 15, 2024")
 */
export function formatReviewDate(date: Date | Timestamp | string | null | undefined): string {
  return formatFirestoreDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }, 'Recent');
}

/**
 * Format date for revision history (short format: "Jan 15")
 */
export function formatRevisionDate(date: Date | Timestamp | string | null | undefined): string {
  return formatFirestoreDate(date, {
    month: 'short',
    day: 'numeric',
  }, 'Recent');
}

/**
 * Format date for published articles (e.g., "January 15, 2024")
 */
export function formatPublishedDate(date: Date | Timestamp | string | null | undefined): string {
  return formatFirestoreDate(date, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }, 'Unknown date');
}

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago")
 * Falls back to absolute date for times > 7 days
 */
export function getRelativeTime(date: Date | Timestamp | string | null | undefined): string {
  const dateObj = toDate(date);
  
  if (!dateObj) {
    return 'Recently';
  }
  
  try {
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    // For dates > 7 days, show absolute date
    return formatFirestoreDate(dateObj, {
      month: 'short',
      day: 'numeric',
      year: diffDays > 365 ? 'numeric' : undefined,
    });
  } catch (error) {
    console.warn('Failed to calculate relative time:', error);
    return 'Recently';
  }
}

/**
 * Format date as a full timestamp (e.g., "Jan 15, 2024, 10:30:45 AM")
 * For detailed timestamps in logs and history
 */
export function formatFullTimestamp(date: Date | Timestamp | string | null | undefined): string {
  return formatFirestoreDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }, 'Unknown time');
}

/**
 * Format date as relative time with fallback to full date
 * Similar to formatRelativeTime but with different plural handling
 * Used for breaking news, articles, and other time-sensitive content
 */
export function formatRelativeDate(date: Date | Timestamp | string | null | undefined): string {
  const dateObj = toDate(date);
  if (!dateObj) return 'Recently';
  
  const now = Date.now();
  const then = dateObj.getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  
  // For older posts, show full date
  return formatFirestoreDate(dateObj, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format date for breaking news (compact format: "5m ago", "2h ago")
 * For recent items shows compact time, older shows "Jan 15" format
 */
export function formatBreakingNewsDate(date: Date | Timestamp | string | null | undefined): string {
  const dateObj = toDate(date);
  if (!dateObj) return 'Recently';
  
  const now = Date.now();
  const then = dateObj.getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  
  return formatFirestoreDate(dateObj, { 
    month: 'short', 
    day: 'numeric' 
  });
}

/**
 * Simple date formatting (e.g., "January 15, 2024")
 * For basic display without time component
 */
export function formatDate(date: Date | Timestamp | string | null | undefined): string {
  return formatFirestoreDate(date, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }, 'Unknown date');
}

/**
 * Format date with time (e.g., "Jan 15, 2024, 10:30 AM")
 * For display with time component
 */
export function formatDateTime(date: Date | Timestamp | string | null | undefined): string {
  return formatFirestoreDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Convert a date value to ISO 8601 string format
 * Handles Firestore Timestamps, Date objects, and string dates
 * Returns timestamps without milliseconds for better sitemap/feed compatibility
 * 
 * Used primarily for XML sitemaps and feeds where consistent date format is required
 * 
 * @param timestamp - The date value to convert (Timestamp, Date, string, or null)
 * @returns ISO 8601 formatted date string (e.g., "2024-01-15T10:30:00.000Z")
 */
export function toISOString(timestamp: Date | Timestamp | string | null | undefined): string {
  if (!timestamp) {
    const now = new Date();
    now.setMilliseconds(0);
    return now.toISOString();
  }
  
  let date: Date;
  
  // Handle Firestore Timestamp
  if (isFirestoreTimestamp(timestamp)) {
    date = timestamp.toDate();
  }
  // Handle string timestamps
  else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      date = new Date();
    }
  }
  // Handle Date objects or numbers
  else if (timestamp instanceof Date) {
    date = timestamp;
  }
  else {
    // Fallback: try to construct Date from whatever we have
    date = new Date(timestamp as unknown as number);
    if (isNaN(date.getTime())) {
      date = new Date();
    }
  }
  
  // Remove milliseconds for cleaner timestamps and better parser compatibility
  date.setMilliseconds(0);
  return date.toISOString();
}

